import { AppModal, Button } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { SlidersHorizontal, X } from "lucide-react";
import { useState, type ReactNode } from "react";

export type AppliedReportFilter = {
  key: string;
  label: string;
  onRemove: () => void;
};

type ReportFilterBarProps = {
  appliedFilters: AppliedReportFilter[];
  children: ReactNode;
  onClearFilters: () => void | Promise<void>;
};

export function ReportFilterBar({
  appliedFilters,
  children,
  onClearFilters,
}: ReportFilterBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeFilterCount = appliedFilters.length;

  return (
    <>
      <div className="flex min-h-8 min-w-0 flex-wrap items-center gap-2">
        {appliedFilters.map((filter) => (
          <Button
            key={filter.key}
            type="button"
            variant="outline"
            className="h-8 max-w-64 rounded-full px-3 text-xs font-medium"
            aria-label={`Remove ${filter.label} filter`}
            onClick={filter.onRemove}
          >
            <span className="truncate">{filter.label}</span>
            <X className="h-3.5 w-3.5 shrink-0" />
          </Button>
        ))}

        <div
          className={cn(
            "relative inline-flex",
            activeFilterCount > 0 && "h-8 overflow-hidden rounded-full border",
          )}
        >
          <Button
            type="button"
            variant={activeFilterCount > 0 ? "ghost" : "outline"}
            className={cn(
              "h-8 px-3 text-xs font-medium",
              activeFilterCount > 0 ? "rounded-none" : "rounded-full",
            )}
            onClick={() => {
              setIsOpen(true);
            }}
          >
            <SlidersHorizontal />
            All filters
            {activeFilterCount > 0 ? (
              <span className="bg-destructive text-destructive-foreground min-w-5 rounded-full px-1 py-0.5 text-center text-xs leading-none">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
          {activeFilterCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              aria-label="Clear filters"
              title="Clear filters"
              className="h-8 w-8 rounded-none border-l p-0"
              onClick={() => {
                void onClearFilters();
              }}
            >
              <X />
            </Button>
          ) : null}
        </div>
      </div>

      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title="All filters"
        size="lg"
        bodyContentClassName="p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="link"
              disabled={activeFilterCount === 0}
              onClick={() => {
                void onClearFilters();
              }}
            >
              Clear all
            </Button>
            <Button
              type="button"
              variant="foreground"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        }
      >
        <div className="p-4">{children}</div>
      </AppModal>
    </>
  );
}
